/** How a list lays out at the current viewport, for sizing a scroll-mode batch. */
export interface RowEstimate {
	/** Items per row. Default 1 (a table). */
	columns?: number;
	/** Height of one row as rendered, gap included, in px. */
	rowHeight: number;
}

const MIN_BATCH = 8;
const MAX_BATCH = 60;

/**
 * Items for one scroll-mode batch: whole rows filling `screens` viewport
 * heights. Two screens means the first batch covers what is visible plus one
 * screen of scrolling — and since the next batch is requested a screen before
 * the end, it arrives before anyone reaches the bottom.
 *
 * An estimate on purpose. Undershooting costs one extra request (the sentinel
 * is still in reach, so the next batch loads straight away); overshooting
 * fetches a few rows early. Neither shows.
 */
export function fitToViewport({ columns = 1, rowHeight }: RowEstimate, screens = 2): number {
	const height = typeof window === 'undefined' ? 900 : window.innerHeight;
	const rows = Math.ceil((height * screens) / Math.max(rowHeight, 1));
	const size = columns * rows;
	// Clamped to whole rows so the last row of a batch is never half empty.
	const min = Math.ceil(MIN_BATCH / columns) * columns;
	const max = Math.floor(MAX_BATCH / columns) * columns;
	return Math.min(Math.max(size, min), max);
}
